// Opportunity Stage is the account's own mapping: an ordered, gapless set of bands over 1–99%, each with
// an admin-typed name, an end bound and a representative win probability. It is configured on the
// Opportunity Stages settings page and read live from the graph — there is no client-side default and no
// fallback table, because a second source of truth could only ever drift from the stored one.
//
// 0% and 100% are not bands. They are fixed boundaries with translated Lost / Won labels, reachable only
// through Mark as Lost / Mark as Won.
//
// baselineWinChance is a fraction 0..1 and is non-null in the database (default 1.00), so null/undefined
// coalesces to 1.00 ⇒ Won. It is rounded to a whole percentage point before being matched against a band.
//
// Kept free of React, Relay and design-system imports so the mapping logic stays cheap to test.

export interface StageBand {
	/** The band's global id — the stable handle a saved filter stores. */
	id: string;
	/** Admin-authored, in the account's own language. Rendered verbatim; there is no key to translate. */
	name: string;
	startPercentage: number;
	endPercentage: number;
	/** The value an opportunity takes when it is moved into this stage. Always inside the band. */
	winProbability: number;
	/** Sampled from the ramp by the band's position in the mapping. See rampColor. */
	color: string;
}

/**
 * The traffic-light progression, as five stops on a continuous gradient rather than a fixed palette.
 * A mapping can hold up to ten bands, so indexing a list of five would repeat hues and destroy the very
 * progression the colours exist to show; sampling instead gives any number of bands a distinct colour,
 * and a five-band mapping lands exactly on the original five.
 */
export const STAGE_RAMP = ['#e5484d', '#f76808', '#f5a623', '#0091ff', '#30a46c'] as const;

/** 0% — outside the ramp entirely: a lost opportunity is not a stage of progress towards winning. */
export const LOST_COLOR = '#a1a1aa';
/** 100% — the ramp's end, so Won and the final band read as the same green. */
export const WON_COLOR = STAGE_RAMP[STAGE_RAMP.length - 1];

export const LOST_MESSAGE_ID = 'opportunities.stage.lost';
export const WON_MESSAGE_ID = 'opportunities.stage.won';

/** A minimal intl surface — only formatMessage is needed. Accepts the real IntlShape or a test stub. */
export interface StageIntl {
	formatMessage: (descriptor: {id: string}) => string;
}

const toChannels = (hex: string): [number, number, number] => [
	parseInt(hex.slice(1, 3), 16),
	parseInt(hex.slice(3, 5), 16),
	parseInt(hex.slice(5, 7), 16),
];

const toHex = (channels: [number, number, number]): string =>
	`#${channels.map(channel => Math.round(channel).toString(16).padStart(2, '0')).join('')}`;

/**
 * The colour for the band at `index` of a mapping holding `count` bands.
 *
 * The ramp is stretched to fit: the first band sits on its first stop, the last on its last, and the rest
 * are evenly spaced points interpolated between the stops in between. Position drives this rather than
 * win probability, because position is what a reader perceives as progression — and since bands are
 * contiguous and ordered, the two agree anyway.
 *
 * A lone band takes the ramp's start; with nothing to progress through there is no progression to show,
 * and picking the end would make a one-stage mapping look permanently won.
 */
export const rampColor = (index: number, count: number): string => {
	if (count <= 1) {
		return STAGE_RAMP[0];
	}
	const clampedIndex = Math.min(Math.max(index, 0), count - 1);
	const position = (clampedIndex / (count - 1)) * (STAGE_RAMP.length - 1);
	const lower = Math.floor(position);
	if (lower >= STAGE_RAMP.length - 1) {
		return STAGE_RAMP[STAGE_RAMP.length - 1];
	}
	const fraction = position - lower;
	if (fraction === 0) {
		return STAGE_RAMP[lower];
	}
	const from = toChannels(STAGE_RAMP[lower]);
	const to = toChannels(STAGE_RAMP[lower + 1]);
	return toHex([
		from[0] + (to[0] - from[0]) * fraction,
		from[1] + (to[1] - from[1]) * fraction,
		from[2] + (to[2] - from[2]) * fraction,
	]);
};

/** The shape of `company.opportunityStageBands` as Relay hands it back, with every level nullable. */
export interface StageBandConnectionLike {
	readonly edges?: ReadonlyArray<{
		readonly node?: {
			readonly id: string;
			readonly name?: string | null;
			readonly startPercentage?: number | null;
			readonly endPercentage?: number | null;
			readonly winProbability?: number | null;
		} | null;
	} | null> | null;
}

/**
 * The account's mapping, ordered by start percentage and coloured by position.
 *
 * Sorted here rather than trusted from the connection: the server returns them in order, but the sort
 * costs nothing and the colour ramp is meaningless if the order is wrong.
 */
export const toStageBands = (connection: StageBandConnectionLike | null | undefined): StageBand[] => {
	const nodes = (connection?.edges ?? []).flatMap(edge => (edge?.node ? [edge.node] : []));
	return nodes
		.map(node => ({
			id: node.id,
			name: node.name ?? '',
			startPercentage: node.startPercentage ?? 0,
			endPercentage: node.endPercentage ?? 0,
			winProbability: node.winProbability ?? 0,
		}))
		.sort((a, b) => a.startPercentage - b.startPercentage)
		.map((band, index, all) => ({...band, color: rampColor(index, all.length)}));
};

/**
 * What a win chance resolves to. `unknown` occurs only for an empty mapping — the server seeds every
 * company on first read, so it is unreachable for a real account, but it is a value rather than a throw
 * so a broken payload degrades to a blank label instead of an unrendered page.
 */
export type StageResolution = {kind: 'lost'} | {kind: 'won'} | {kind: 'band'; band: StageBand} | {kind: 'unknown'};

/** The whole percentage point a stored win chance sits on. Unset means 1.00 (Won), per the DB default. */
export const toPercent = (baselineWinChance: number | null | undefined): number => Math.round((baselineWinChance ?? 1) * 100);

/**
 * The stage for a stored win chance (a 0..1 fraction).
 *
 * The server guarantees the bands are contiguous across 1–99, so every value in that range lands in
 * exactly one band and the gap fallback below is unreachable for valid data. It is still there because a
 * malformed payload should show a slightly wrong label rather than an empty list.
 */
export const bandForWinChance = (bands: StageBand[], baselineWinChance: number | null | undefined): StageResolution => {
	const percent = toPercent(baselineWinChance);
	if (percent <= 0) {
		return {kind: 'lost'};
	}
	if (percent >= 100) {
		return {kind: 'won'};
	}
	if (bands.length === 0) {
		return {kind: 'unknown'};
	}
	const containing = bands.find(band => percent >= band.startPercentage && percent <= band.endPercentage);
	if (containing) {
		return {kind: 'band', band: containing};
	}
	// A gap. The nearest band below is the closest thing to the truth; below every band, the first one.
	const below = [...bands].reverse().find(band => band.startPercentage <= percent);
	return {kind: 'band', band: below ?? bands[0]};
};

/** A band's range as shown to the user, e.g. "40–59%" (en dash, matching the rest of the feature). */
export const rangeLabel = (band: StageBand): string => `${band.startPercentage}–${band.endPercentage}%`;

/** The stage name for a win chance: the band's own name, or the translated Lost / Won label. */
export const stageLabel = (bands: StageBand[], baselineWinChance: number | null | undefined, intl: StageIntl): string => {
	const resolved = bandForWinChance(bands, baselineWinChance);
	switch (resolved.kind) {
		case 'lost':
			return intl.formatMessage({id: LOST_MESSAGE_ID});
		case 'won':
			return intl.formatMessage({id: WON_MESSAGE_ID});
		case 'band':
			return resolved.band.name;
		default:
			return '';
	}
};

export const stageColor = (bands: StageBand[], baselineWinChance: number | null | undefined): string => {
	const resolved = bandForWinChance(bands, baselineWinChance);
	switch (resolved.kind) {
		case 'lost':
			return LOST_COLOR;
		case 'won':
			return WON_COLOR;
		case 'band':
			return resolved.band.color;
		default:
			return LOST_COLOR;
	}
};

/**
 * The range label + colour for a win chance. The list's Win Probability column renders the exact
 * percentage tinted in this colour; the range is what the modal picker and the quick-action toasts show.
 */
export interface WinProbabilityBand {
	label: string;
	color: string;
}

export const winBand = (bands: StageBand[], baselineWinChance: number | null | undefined): WinProbabilityBand => {
	const resolved = bandForWinChance(bands, baselineWinChance);
	switch (resolved.kind) {
		case 'lost':
			return {label: '0%', color: LOST_COLOR};
		case 'won':
			return {label: '100%', color: WON_COLOR};
		case 'band':
			return {label: rangeLabel(resolved.band), color: resolved.band.color};
		default:
			return {label: '', color: LOST_COLOR};
	}
};

// TNSR-635 review (Lakshmi): previously defined independently in both FinancialSetupSection.tsx
// (the read-only Retainer preview) and AiProjectPlanningModal.tsx (the submit-time period-creation
// loop) - identical today, with nothing linking them. A future correction to one (a different
// clamping rule, a leap-year edge case, moving to a date library) would silently reintroduce the
// preview/submission divergence that having two independent copies exists to prevent. Hoisted here,
// this folder's established React-free helper home, so the invariant is structural rather than a
// convention two comments ask readers to honour.
//
// Plain setMonth() OVERFLOWS instead of clamping - Jan 31 + 1 month becomes Mar 3 (February has no
// 31st), not Feb 28 - diverging from Java's LocalDate.plusMonths. Moving to the 1st of the target
// month before reading its length, rather than letting the day ride along, is what keeps the
// overflow from happening in the first place.
export const addMonthsClamped = (date: Date, months: number): Date => {
	const day = date.getDate();
	const firstOfTargetMonth = new Date(date.getFullYear(), date.getMonth() + months, 1);
	const daysInTargetMonth = new Date(firstOfTargetMonth.getFullYear(), firstOfTargetMonth.getMonth() + 1, 0).getDate();
	firstOfTargetMonth.setDate(Math.min(day, daysInTargetMonth));
	return firstOfTargetMonth;
};
